import {
  OrderSide,
  OrderType,
  PlaceOrderPayload,
  TimeInForce,
  TradingOrder,
} from '../../services/trading-service'

export type OrderForm = {
  side: OrderSide
  type: OrderType
  timeInForce: TimeInForce
  /** Order by number of shares or by dollar amount. */
  mode: 'qty' | 'notional'
  amount: string
  limitPrice: string
  stopPrice: string
  trailBy: 'price' | 'percent'
  trail: string
  extendedHours: boolean
}

export const emptyOrderForm = (side: OrderSide = 'buy'): OrderForm => ({
  side,
  type: 'market',
  timeInForce: 'day',
  mode: 'qty',
  amount: '',
  limitPrice: '',
  stopPrice: '',
  trailBy: 'percent',
  trail: '',
  extendedHours: false,
})

export const ORDER_TYPE_LABEL: Record<OrderType, string> = {
  market: 'Market',
  limit: 'Limit',
  stop: 'Stop',
  stop_limit: 'Stop limit',
  trailing_stop: 'Trailing stop',
}

export const TIME_IN_FORCE_LABEL: Record<TimeInForce, string> = {
  day: 'Day',
  gtc: 'Good till cancelled',
  ioc: 'Immediate or cancel',
  fok: 'Fill or kill',
  opg: 'Market open',
  cls: 'Market close',
}

export const ORDER_TYPE_HELP: Record<OrderType, string> = {
  market: 'Buys or sells right away at the best available price.',
  limit: 'Fills only at your limit price or better.',
  stop: 'Becomes a market order once the price reaches your stop price.',
  stop_limit: 'Becomes a limit order once the price reaches your stop price.',
  trailing_stop:
    'A stop that follows the price by a fixed amount or percent, and triggers when the price turns against you by that much.',
}

export const usesLimit = (type: OrderType) => type === 'limit' || type === 'stop_limit'
export const usesStop = (type: OrderType) => type === 'stop' || type === 'stop_limit'

const isWhole = (n: number) => Number.isInteger(Number(n.toFixed(8)))

const positive = (value: string) => {
  const n = Number(value)
  return value.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null
}

/** Dollar-amount orders are market orders only (Alpaca's rule). */
export const allowsDollars = (type: OrderType) => type === 'market'

export const allowsExtendedHours = (form: Pick<OrderForm, 'type' | 'timeInForce'>) =>
  form.type === 'limit' && form.timeInForce === 'day'

/** Time-in-force choices for the order as it stands, following Alpaca's rules. */
export const allowedTimeInForce = (form: OrderForm): TimeInForce[] => {
  const qty = positive(form.amount)
  if (form.mode === 'notional' || form.extendedHours) return ['day']
  if (form.mode === 'qty' && qty !== null && !isWhole(qty)) return ['day']
  if (form.type === 'trailing_stop') return ['day', 'gtc']
  if (usesStop(form.type)) return ['day', 'gtc', 'ioc', 'fok']
  return ['day', 'gtc', 'ioc', 'fok', 'opg', 'cls']
}

/**
 * Brings dependent fields back in line after a change: dollar mode only for
 * market orders, extended hours only for day limit orders, and a
 * time-in-force the order allows.
 */
export const normalizeForm = (form: OrderForm): OrderForm => {
  let next = form
  if (next.mode === 'notional' && !allowsDollars(next.type)) next = { ...next, mode: 'qty' }
  if (next.extendedHours && next.type !== 'limit') next = { ...next, extendedHours: false }
  const allowed = allowedTimeInForce(next)
  if (!allowed.includes(next.timeInForce)) next = { ...next, timeInForce: allowed[0] }
  return next
}

/**
 * Why the order cannot be reviewed yet, or null when it can. Mirrors the
 * server's checks so mistakes show before anything is sent.
 */
export const formProblem = (form: OrderForm, sharesAvailable: number | null): string | null => {
  const amount = positive(form.amount)
  if (amount === null) {
    return form.mode === 'qty' ? 'Enter the number of shares.' : 'Enter an amount in USD.'
  }
  if (usesLimit(form.type) && positive(form.limitPrice) === null) {
    return 'Enter a limit price.'
  }
  if (usesStop(form.type) && positive(form.stopPrice) === null) {
    return 'Enter a stop price.'
  }
  if (form.type === 'trailing_stop') {
    const trail = positive(form.trail)
    if (trail === null) return 'Enter how far the stop trails the price.'
    if (form.trailBy === 'percent' && trail > 100) return 'The trail percent must be 100 or less.'
  }
  if (form.type === 'trailing_stop' && form.mode === 'qty' && !isWhole(amount)) {
    return 'Trailing stop orders need a whole number of shares.'
  }
  if (!allowedTimeInForce(form).includes(form.timeInForce)) {
    return `${TIME_IN_FORCE_LABEL[form.timeInForce]} is not available for this order.`
  }
  if (form.extendedHours && !allowsExtendedHours(form)) {
    return 'Extended hours is only for day limit orders.'
  }
  if (form.side === 'sell' && form.mode === 'qty' && sharesAvailable !== null) {
    if (sharesAvailable <= 0) return 'You have no shares of this stock to sell.'
    if (amount > sharesAvailable + 1e-9) return `You can sell up to ${sharesAvailable} shares.`
  }
  return null
}

export const toPayload = (symbol: string, form: OrderForm): PlaceOrderPayload => {
  const amount = Number(form.amount)
  return {
    symbol,
    side: form.side,
    type: form.type,
    timeInForce: form.timeInForce,
    ...(form.mode === 'qty' ? { qty: amount } : { notional: amount }),
    ...(usesLimit(form.type) ? { limitPrice: Number(form.limitPrice) } : {}),
    ...(usesStop(form.type) ? { stopPrice: Number(form.stopPrice) } : {}),
    ...(form.type === 'trailing_stop'
      ? form.trailBy === 'price'
        ? { trailPrice: Number(form.trail) }
        : { trailPercent: Number(form.trail) }
      : {}),
    ...(allowsExtendedHours(form) && form.extendedHours ? { extendedHours: true } : {}),
  }
}

export const usd = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === ''
    ? '—'
    : Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

/** "Limit @ $90.00", "Stop limit: stop $9.00, limit $8.50", "Trailing stop 5%". */
export const describeOrderType = (
  o: Pick<TradingOrder, 'type' | 'limit_price' | 'stop_price' | 'trail_price' | 'trail_percent'>,
): string => {
  const label = ORDER_TYPE_LABEL[o.type] ?? o.type
  switch (o.type) {
    case 'limit':
      return `${label} @ ${usd(o.limit_price)}`
    case 'stop':
      return `${label} @ ${usd(o.stop_price)}`
    case 'stop_limit':
      return `${label}: stop ${usd(o.stop_price)}, limit ${usd(o.limit_price)}`
    case 'trailing_stop':
      return o.trail_percent
        ? `${label} ${Number(o.trail_percent)}%`
        : `${label} ${usd(o.trail_price)}`
    default:
      return label
  }
}

/** Open orders that can be modified: anything but market orders. */
export const isModifiable = (o: Pick<TradingOrder, 'type'>) => o.type !== 'market'

export const OPEN_ORDER_DONE = [
  'filled',
  'canceled',
  'expired',
  'rejected',
  'replaced',
  'done_for_day',
]

export const isOpenOrder = (o: Pick<TradingOrder, 'status'>) =>
  !OPEN_ORDER_DONE.includes(o.status.toLowerCase())
